import MonacoEditor, { useMonaco } from "@monaco-editor/react";
import { useCallback, useEffect, useRef } from "react";

interface CustomMonacoEditorProps {
	value: string | undefined;
	onChange: (value: string | undefined) => void;
	height?: string;
	language?: string;
	readOnly?: boolean;
}

const CustomMonacoEditor: React.FC<CustomMonacoEditorProps> = ({
	value,
	onChange,
	height = "150px",
	language = "json",
	readOnly = false
}) => {
	const monaco = useMonaco();
	const timeoutRef = useRef<NodeJS.Timeout>(null);

	const handleChange = useCallback(
		(value: string | undefined) => {
			// Clear any existing timeout
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			// For non-JSON editors, pass through the value immediately
			if (language !== "json") {
				onChange(value);
				return;
			}

			// For JSON, validate and only call onChange if it's valid
			timeoutRef.current = setTimeout(() => {
				if (!value) {
					onChange(value);
					return;
				}

				try {
					JSON.parse(value);
					onChange(value);
				} catch {
					// If invalid JSON, don't update the parent
					console.debug("Invalid JSON, skipping update");
				}
			}, 500); // Debounce for 500ms
		},
		[language, onChange]
	);

	useEffect(() => {
		if (monaco) {
			monaco.editor.defineTheme("custom-light", {
				base: "vs",
				inherit: true,
				rules: [],
				colors: {
					"editor.background": "#ffffff",
					"editor.foreground": "#333333"
				}
			});

			monaco.editor.setTheme("custom-light");
		}
	}, [monaco]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<MonacoEditor
			height={height}
			defaultLanguage={language}
			value={value}
			onChange={handleChange}
			theme="custom-light"
			options={{
				minimap: { enabled: false },
				fontFamily: "Poppins",
				fontSize: 14,
				lineNumbers: "off",
				padding: { top: 12 },
				readOnly,
				scrollBeyondLastLine: false,
				overviewRulerLanes: 0,
				renderLineHighlight: "none",
				scrollbar: {
					verticalScrollbarSize: 6,
					horizontalScrollbarSize: 6
				}
			}}
		/>
	);
};

export default CustomMonacoEditor;
